import { HTTPError } from "ky";
import { z } from "zod";

import type { ApiError } from "~/shared/types/api-types.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

const VALIDATION_STATUSES = new Set([400, 409, 412, 422]);

const SERVICE_UNAVAILABLE_STATUSES = new Set([429, 500, 502, 503, 504]);

export const FTL_TENDER_ERROR_MESSAGES = {
	validation:
		"We could not tender this quote right now. Please review the shipment details and try again.",
	serviceUnavailable:
		"We could not tender this quote right now. Please try again in a few minutes.",
	fallback: "We could not tender this quote right now. Please try again.",
	persistentFailureGuidance: "If the problem continues, contact support.",
} as const;

type ErrorWithStatus = {
	response?: {
		status?: unknown;
	};
};

function getHttpStatus(error: unknown): number | null {
	if (!error || typeof error !== "object") {
		return null;
	}

	const typedError = error as ErrorWithStatus;
	if (!typedError.response || typeof typedError.response !== "object") {
		return null;
	}

	const { status } = typedError.response;
	return typeof status === "number" ? status : null;
}

function isNetworkOrTransportError(error: unknown): boolean {
	if (!(error instanceof Error)) {
		return false;
	}

	const normalizedMessage = error.message.toLowerCase();
	if (
		normalizedMessage.includes("failed to fetch") ||
		normalizedMessage.includes("network") ||
		normalizedMessage.includes("fetch failed") ||
		normalizedMessage.includes("load failed") ||
		normalizedMessage.includes("timeout")
	) {
		return true;
	}

	return error instanceof TypeError;
}

function withPersistentFailureGuidance(
	message: string,
	consecutiveFailureCount?: number,
): string {
	if ((consecutiveFailureCount ?? 0) <= 3) {
		return message;
	}

	return `${message} ${FTL_TENDER_ERROR_MESSAGES.persistentFailureGuidance}`;
}

function createNormalizedApiError(
	message: string,
	error: unknown,
	status: number | null,
): ApiError {
	const normalizedError: ApiError = {
		success: false,
		error: message,
		details: error,
	};

	if (status !== null) {
		normalizedError.status = status;
	}

	return normalizedError;
}

// Matches the `[{ code, message }]` shape LtlQuoting/QuoteMgmt send on a 400
// from tender (expired quote, missing Customer PO, tendered-by checks, etc).
// extractApiError above only understands object-shaped bodies, so a 400's
// real detail never reaches it - this is a separate, narrower extraction
// just for the top-level array shape.
const ftlTenderValidationErrorSchema = z.array(
	z.object({
		code: z.string(),
		message: z.string(),
	}),
);

async function extractValidationErrorMessages(
	error: unknown,
): Promise<string[] | null> {
	if (!(error instanceof HTTPError)) {
		return null;
	}

	const contentType = error.response.headers.get("content-type") ?? "";
	if (!contentType.toLowerCase().includes("application/json")) {
		return null;
	}

	try {
		const body: unknown = await error.response.clone().json();
		const parsed = ftlTenderValidationErrorSchema.safeParse(body);
		if (parsed.success && parsed.data.length > 0) {
			return parsed.data.map((validationError) => validationError.message);
		}
	} catch {
		// Not JSON, or didn't match the expected shape - fall back below.
	}

	return null;
}

export async function mapFtlTenderErrorToUserMessage(
	error: unknown,
	consecutiveFailureCount?: number,
): Promise<ApiError> {
	const apiError = await extractApiError(error);
	const httpStatus = apiError?.status ?? getHttpStatus(error);

	if (httpStatus === 400) {
		const messages = await extractValidationErrorMessages(error);
		if (messages) {
			return createNormalizedApiError(
				withPersistentFailureGuidance(
					`${FTL_TENDER_ERROR_MESSAGES.validation} ${messages.join(" ")}`,
					consecutiveFailureCount,
				),
				error,
				httpStatus,
			);
		}

		return createNormalizedApiError(
			withPersistentFailureGuidance(
				FTL_TENDER_ERROR_MESSAGES.validation,
				consecutiveFailureCount,
			),
			error,
			httpStatus,
		);
	}

	if (httpStatus !== null && VALIDATION_STATUSES.has(httpStatus)) {
		return createNormalizedApiError(
			withPersistentFailureGuidance(
				FTL_TENDER_ERROR_MESSAGES.validation,
				consecutiveFailureCount,
			),
			error,
			httpStatus,
		);
	}

	if (
		(httpStatus !== null &&
			(SERVICE_UNAVAILABLE_STATUSES.has(httpStatus) || httpStatus >= 500)) ||
		isNetworkOrTransportError(error)
	) {
		return createNormalizedApiError(
			withPersistentFailureGuidance(
				FTL_TENDER_ERROR_MESSAGES.serviceUnavailable,
				consecutiveFailureCount,
			),
			error,
			httpStatus,
		);
	}

	return createNormalizedApiError(
		withPersistentFailureGuidance(
			FTL_TENDER_ERROR_MESSAGES.fallback,
			consecutiveFailureCount,
		),
		error,
		httpStatus,
	);
}
